import { storage } from '@/data/firebase/config';
import {
  getDownloadURL,
  ref,
  uploadBytes,
  UploadMetadata,
} from '@firebase/storage';

type UploadFileProps = {
  file: File;
  path: string;
  metadata?: Record<string, string>;
};

/**
 * Obtiene las dimensiones de una imagen si el archivo es una imagen
 */
const getImageDimensions = (
  file: File,
): Promise<{ width: number; height: number } | null> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(null);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };

    img.src = url;
  });
};

const uploadFile = async ({
  file,
  path,
  metadata = {},
}: UploadFileProps): Promise<string> => {
  try {
    const fileExtension = file.name.split('.').pop();
    const uniqueId = Date.now().toString();
    const newFileName = `${uniqueId}.${fileExtension}`;
    const storageRef = ref(storage, `${path}/${newFileName}`);

    // Obtener dimensiones si es una imagen
    const dimensions = await getImageDimensions(file);

    // Construir metadatos personalizados
    const customMetadata: Record<string, string> = {
      ...metadata,
      uploadedAt: new Date().toISOString(),
    };

    if (dimensions) {
      customMetadata.width = dimensions.width.toString();
      customMetadata.height = dimensions.height.toString();
      customMetadata.aspectRatio = (
        dimensions.width / dimensions.height
      ).toFixed(2);
    }

    const uploadMetadata: UploadMetadata = {
      contentType: file.type,
      customMetadata,
    };

    const snapshot = await uploadBytes(storageRef, file, uploadMetadata);
    const downloadURL = await getDownloadURL(snapshot.ref);

    console.log('Archivo subido con metadatos:', {
      url: downloadURL,
      dimensions,
      size: file.size,
      contentType: file.type,
    });

    return downloadURL;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export default uploadFile;
